






















































































































































































































import Vue from "vue";
import { UserGroup, UserGroupType, UserType } from "@infoland/iprova-api";
import ItemPool, { isTeam, isUser, isUserGroup, isUserOutOfOffice } from "./Functions";
import { ItemPoolResult, UserSelectionItem, UserSelectionType, UserSelectorOptions } from "./types";
import UserCard from "./UserCard.vue";
import UserGroupCard from "./UserGroupCard.vue";
import TeamCard from "./TeamCard.vue";
import { TranslateResult } from "vue-i18n";
import storeHelper from "../../../store/store-helper";
import { CancelableDebouncedFunction, Debouncer, UserSelectorFilterOptions } from "@/helpers/newUserSelector";
import { props } from "./types";

export default Vue.extend({
    name: "AdvancedUserSelector",
    components: { UserCard, UserGroupCard, TeamCard },
    model: {
        prop: "value",
        event: "update:selectedItems"
    },
    props: {
        ...props,
        /** The maximum height of the user selector */
        maxHeight: { type: Number, default: 200 },
        /** The selected items (also, the model) */
        value: { type: Array as () => UserSelectionItem[], default: [] }
    },
    data: () => {
        return {
            /** The item pool to pull items for the user selector from */
            itemPool: null as ItemPool,
            /** The result from getting items */
            itemPoolResult: {} as ItemPoolResult,
            /** Whether the user selector is loading the initial batch of items */
            loading: true,
            /** The name of items to search for */
            searchName: "",
            /** The selected user group ? */
            userGroupSelection: null as UserSelectionItem,
            /** Whether to show selected items */
            showSelected: false,
            /** Heights of interface elements other than the list of users */
            otherHeights: 0 as number,
            /** The currently shown tooltip item */
            tooltipItem: null as UserSelectionItem,
            /** Filter options for the user selector */
            userSelectorFilterOptions: null as UserSelectorFilterOptions,
            /** The user groups to show at the top as a filter option */
            userGroups: [] as UserSelectionItem<UserGroup>[],
            /** A search for user groups ? */
            userGroupSearch: "",
            /** Whether a user group is loading ? */
            userGroupLoading: false,
            /** Whether more items are being loaded */
            loadingMore: false as boolean,
            /** The timeout for loading more items */
            loadMoreTimeout: 0 as number,
            /** The debounced item load function */
            loadItemsDebounce: null as (() => void) & CancelableDebouncedFunction,
            /** The debounced group filter load function */
            loadUserGroupsDebounce: null as (() => void) & CancelableDebouncedFunction,
            /** Whether the scrollhandler has been added to the datatable */
            scrollHandlerAdded: false as boolean,
            /** The internal list of selected items */
            selectedItems: [] as UserSelectionItem[]
        };
    },
    computed: {
        /** Indicates that this is a small-screen (mobile) device */
        isMobile(): boolean {
            return this.$vuetify.breakpoint.smAndDown;
        },
        /** gets the icons to use for the checkbox */
        checkboxIcons(): { onIcon: string; offIcon: string } {
            if (this.multiple) {
                //set undefined to use default icons
                return {
                    onIcon: undefined,
                    offIcon: undefined
                };
            } else {
                return {
                    onIcon: "radio_button_checked",
                    offIcon: "radio_button_unchecked"
                };
            }
        },
        /** Gets whether users can be selected */
        usersAllowed(): boolean {
            return this.allowedTypes.includes("user");
        },
        /** Gets the maximum height of the datatable */
        maxDataTableHeight(): number {
            return this.maxHeight - this.otherHeights;
        },
        /** Gets the maximum height of the datatable as a style string */
        maxDataTableHeightString(): string {
            return this.maxDataTableHeight + "px";
        },
        /** Whether to show the user in user group filter option */
        showUserInUserGroupFilterOption(): boolean {
            return this.usersAllowed && this.userSelectorFilterOptions && this.userSelectorFilterOptions.users_in_user_group;
        },
        /** The text to show when there is no data */
        noDataText(): TranslateResult {
            if (this.loading || this.userGroupLoading) return this.$t("components.userselector.loading");
            else return this.$t("components.general.noData");
        },
        /** Gets the items to show in the dialog */
        items(): UserSelectionItem[] {
            if (this.showSelected) return this.selectedItems;
            else return this.itemPoolResult.items || [];
        },
        /** Whether the selection filter should be visible */
        showingSelectionFilter(): boolean {
            return this.multiple && this.selectedItems.length > 0;
        },
        /** Gets the headers for the user selector's table of users (and groups, and teams) */
        headers(): { text: string; value: string; width?: string; align?: string; sortable: boolean; class?: string }[] {
            const headers = [];
            headers.push({
                text: "",
                value: "icon",
                width: "1%",
                align: "start",
                sortable: false,
                class: "i-header-no-padding-left-right"
            });
            headers.push({
                text: this.$t("components.userselector.name") as string,
                value: "name",
                align: "start",
                width: this.usersAllowed ? "50%" : "100%",
                sortable: false
            });
            if (this.usersAllowed)
                headers.push({
                    text: this.$t("components.userselector.username") as string,
                    width: "50%",
                    value: "data.login_code",
                    sortable: false
                });
            headers.push({ text: "", value: "info", width: "1%", align: "end", sortable: false });

            return headers;
        }
    },
    watch: {
        /** Watches for typing in the name to search for */
        searchName(): void {
            this.loadItemsDebounce();
        },
        /** Checks for a change to user group selector (user group filter) ? */
        userGroupSearch(): void {
            this.loadUserGroupsDebounce();
        },
        /** recalculate heights if the showing of the selection filter has changed */
        showingSelectionFilter(): void {
            this.calculateOtherHeights();
        },
        /** watches for changes to value (model) */
        value(newValue: UserSelectionItem[]): void {
            if (newValue) this.selectedItems = newValue;
        },
        /** watch for changes to selected items to see whether showing selected items should be turned off */
        selectedItems(newValue: UserSelectionItem[]): void {
            if (newValue.length == 0) this.showSelected = false;
            this.$emit("update:selectedItems", newValue);
        }
    },
    mounted(): void {
        this.selectedItems = this.value || [];
        this.initAsync();
    },
    beforeDestroy(): void {
        if (this.loadItemsDebounce) this.loadItemsDebounce.cancel();
        if (this.loadUserGroupsDebounce) this.loadUserGroupsDebounce.cancel();
        if (this.itemPool) this.itemPool.destroy();
    },
    updated(): void {
        // calculate the height of all elements except the datatable
        this.calculateOtherHeights();
    },
    methods: {
        /** Creates a new item pool (makes testing easier)
         * @returns a new item pool object \
         */
        createItemPool(): ItemPool {
            return new ItemPool();
        },
        /** quickly selects an item in single select mode and closes the dialog
         * @param item The item to select quickly
         */
        quickSelect(item: UserSelectionItem): void {
            if (!this.multiple) {
                this.selectedItems.splice(0, this.selectedItems.length, item);
                this.$emit("finish");
            }
        },
        /** calculates the otherHeights value (on the next tick) */
        calculateOtherHeights(): void {
            this.$nextTick(() => {
                if (this.$refs.container && this.$refs.dataTable) {
                    const container: HTMLDivElement = this.$refs.container as HTMLDivElement;
                    const table: HTMLElement = (this.$refs.dataTable as Vue).$el as HTMLElement;

                    table.style.display = "none";
                    this.otherHeights = container.offsetHeight;
                    table.style.display = "";
                } else {
                    this.otherHeights = 0;
                }
            });
        },
        /** Called when the select-all function is used (to stop showing selected items if no items are selected anymore)
         * @param e The event data, containing the selected items and whether select-all is true or false
         */
        selectAllClicked(e: { items: UserSelectionItem[]; value: boolean }): void {
            if (e.value == false) this.showSelected = false;
        },
        /** Toggles between showing all items and selected items */
        toggleShowingSelectedItems(): void {
            this.showSelected = !this.showSelected;
            this.loadSelectedItemDetailsAsync();
        },
        /** Sets the current tooltip item to the given item
         * @param item The item (user, group, team) to set as the tooltip item
         */
        setTooltipItem(item: UserSelectionItem): void {
            // Timeout 0 to make sure you can open a usercard when another one is already open
            setTimeout(() => {
                this.tooltipItem = item;
            }, 0);
        },
        /** returns whether the given item is a user
         * @param item the item to determine the type of
         * @returns true if the item is a user, otherwise false
         */
        itemIsUser(item: UserSelectionItem): boolean {
            return isUser(item);
        },
        /** returns whether the given item is a group
         * @param item the item to determine the type of
         * @returns true if the item is a user group, otherwise false
         */
        itemIsUserGroup(item: UserSelectionItem): boolean {
            return isUserGroup(item);
        },
        /** returns whether the given item is a team
         * @param item the item to determine the type of
         * @returns true if the item is a team, otherwise false
         */
        itemIsTeam(item: UserSelectionItem): boolean {
            return isTeam(item);
        },
        /** attaches the 'load-on-scroll' handler to the datatable list */
        attachScrollHandler(): void {
            const dataTableRef: Vue = this.$refs.dataTable as Vue;
            if (!dataTableRef) {
                setTimeout(this.attachScrollHandler.bind(this), 250);
                return;
            }

            // don't add this more than once
            if (this.scrollHandlerAdded) return;

            this.scrollHandlerAdded = true;
            const dataTable: HTMLElement = dataTableRef.$el as HTMLElement;
            const scrollContainer: HTMLElement = dataTable.querySelector(".v-data-table__wrapper");
            scrollContainer.addEventListener("scroll", this.onScroll);
        },
        /** Handles scrolling in the datatable list
         * @param e The scroll event triggering this
         */
        onScroll(e: Event): void {
            if (this.loadingMore) return;
            if (!this.itemPoolResult.moreItemsAvailable) return;
            const offsetHeight: number = (e.target as HTMLElement).offsetHeight;
            const scrollHeight: number = (e.target as HTMLElement).scrollHeight;
            const scrollTop: number = (e.target as HTMLElement).scrollTop;
            if (scrollTop > (scrollHeight - offsetHeight) * 0.75) {
                if (this.loadMoreTimeout) clearTimeout(this.loadMoreTimeout);

                this.loadMoreTimeout = window.setTimeout(this.loadMoreAsync, 250);
            }
        },
        /** Loads more items */
        async loadMoreAsync(): Promise<void> {
            this.loadMoreTimeout = null;
            if (!this.loadingMore) {
                await this.loadItemsAsync(false);
            }

            this.loadingMore = false;
        },
        /** Closes any popup cards */
        closeCards(): void {
            this.tooltipItem = null;
        },
        /** Triggers when a new user group for filtering users is selected. Reloads items */
        onUserGroupSelectionChange(): void {
            this.userGroupSearch = "";
            this.loadItemsDebounce();
        },
        /** Creates the load user groups function with a debounce time of 500 ms
         * @returns the debounced function wrapper
         */
        getLoadUserGroupsDebounce(): (() => Promise<void>) & CancelableDebouncedFunction {
            return Debouncer.debounce(this.loadUserGroupsAsync, 500);
        },
        /** Loads user groups for the user group filter list */
        async loadUserGroupsAsync(): Promise<void> {
            this.userGroupLoading = true;
            const groupPool: ItemPool = this.createItemPool();
            groupPool.setSearchParameters({ nameLoginCodeEmailContains: this.userGroupSearch, allowedTypes: ["usergroup"], allowedSystemItems: [] });
            groupPool.setBatchSize(Infinity);
            const result: ItemPoolResult = await groupPool.getItemsAsync();
            this.userGroups = result.items as UserSelectionItem<UserGroup>[];
            this.userGroupLoading = false;
        },
        /** Creates the load items function with a debounce time of 500 ms
         * @returns the wrapped debounce function
         */
        getLoadItemsDebounce(): (() => Promise<void>) & CancelableDebouncedFunction {
            return Debouncer.debounce(this.loadItemsAsync, 500);
        },
        /** Loads items for the user selector
         * @param startNewSearch If this is true, loading of items is started from the beginning. If it is false additional items are loaded
         */
        async loadItemsAsync(startNewSearch: boolean = true): Promise<void> {
            if (startNewSearch) {
                this.itemPoolResult = {} as ItemPoolResult;
                this.loading = true;
                this.closeCards();

                let userGroupID: string = null;
                let allowedTypes: UserSelectionType[] = this.allowedTypes;
                if (this.userGroupSelection) {
                    userGroupID = (this.userGroupSelection.data as UserGroup).user_group_id;
                    allowedTypes = ["user"];
                }

                this.itemPool.setSearchParameters({
                    nameLoginCodeEmailContains: this.searchName,
                    userGroupID: userGroupID,
                    allowedTypes: allowedTypes,
                    allowedSystemItems: this.allowedSystemItems,
                    allowedUserIDs: this.allowedUserIDs,
                    allowedUserGroupIDs: this.allowedUserGroupIDs,
                    allowedTeamIDs: this.allowedTeamIDs,
                    excludedIDs: this.excludedIDs,
                    excludeWithoutEmailAddress: this.requireEmail == "yes",
                    excludeInactive: true,
                    userAccountType: this.userAccountTypeFilter
                });
            }

            this.itemPool.setBatchSize(Math.max(50, Math.floor(this.maxDataTableHeight / 48) * 2));

            this.loadingMore = true;

            // actual call to get items
            const result: ItemPoolResult = await this.itemPool.getItemsAsync();

            // check if this queue item was cancelled
            if (result === null) return;

            // not cancelled, time to process
            this.itemPoolResult = result;

            // rebuild selection (if multiple select)
            if (this.multiple) {
                if (this.selectedItems.length > 0) {
                    // make a mapping from item_id to boolean to use as a hash lookup in the loop below
                    const itemIsSelectedMap: Record<string, boolean> = this.selectedItems.reduce((map, item) => {
                        map[item.item_id] = true;
                        return map;
                    }, {} as Record<string, boolean>);

                    for (let i: number = 0; i < this.itemPoolResult.items.length; i++) {
                        const item: UserSelectionItem = this.itemPoolResult.items[i];
                        // check if the item is selected (in this.selecteditems) and make sure the instance of the item in selecteditems becomes
                        // the same instance as the one in the item pool result. The datatable needs these to instances to be the same (pointerwise)
                        if (itemIsSelectedMap[item.item_id]) {
                            const index: number = this.selectedItems.findIndex(findItem => findItem.item_id == item.item_id);
                            if (index > -1) {
                                this.selectedItems.splice(index, 1, item);
                            }
                        }
                    }
                }
            } else {
                if (this.selectedItems.length > 0) {
                    // single select, only keep selection if the selected item is still in the results
                    const index: number = this.itemPoolResult.items.findIndex(item => item.item_id == this.selectedItems[0].item_id);
                    if (index > -1) {
                        this.selectedItems[0] = this.itemPoolResult.items[index];
                    } else {
                        this.selectedItems.splice(0, this.selectedItems.length);
                    }
                }
            }
            this.loading = false;
            this.loadingMore = false;
            this.showSelected = false;
        },
        /** Loads details for selected items (if they are not yet loaded)
         */
        async loadSelectedItemDetailsAsync(): Promise<void> {
            const itemsToLoad: string[] = [];
            for (let i: number = 0; i < this.selectedItems.length; i++) {
                if (!this.selectedItems[i].data) itemsToLoad.push(this.selectedItems[i].item_id);
            }

            if (itemsToLoad.length > 0) {
                const itemPool: ItemPool = this.createItemPool();
                itemPool.setBatchSize(itemsToLoad.length);
                itemPool.setSearchParameters({
                    allowedTypes: this.allowedTypes,
                    allowedSystemItems: this.allowedSystemItems,
                    allowedUserIDs: itemsToLoad,
                    allowedUserGroupIDs: itemsToLoad,
                    allowedTeamIDs: itemsToLoad,
                    excludeInactive: true
                });

                this.loadingMore = true;

                // actual call to get items
                const result: ItemPoolResult = await itemPool.getItemsAsync();

                // check first if this queue item was cancelled
                if (result === null) return;

                // rebuild selection (if multiple select)
                for (let i: number = 0; i < result.items.length; i++) {
                    const item: UserSelectionItem = result.items[i];
                    const oldItem: UserSelectionItem = this.selectedItems.find(findItem => findItem.item_id == item.item_id);
                    if (oldItem) oldItem.data = item.data;
                }
            }
            this.loadingMore = false;
        },
        /** Gets the color for the item icon
         * @param item the item to get the icon color for
         * @returns the color for the item icon, or error if the item is illegal
         */
        getIconColor(item: UserSelectionItem): string {
            if (!item.data) return "grey";
            if (isUser(item)) {
                if (item.data.user_type === UserType.syncedUser) return "green";
                if (item.data.user_type === UserType.systemUser) return "red";
                if ((item.data.user_type as string) === "service_principal") return "brown";
                if ((item.data.user_type as string) === "system_service_principal") return "red";
                return "blue";
            } else if (isUserGroup(item)) {
                if (item.data.user_group_type === UserGroupType.synced_user_group) return "green";
                if (item.data.user_group_type === UserGroupType.system_user_group) return "red";
                return "blue";
            } else if (isTeam(item)) {
                return "blue";
            } else {
                return "error";
            }
        },
        /** Gets the icon for a user selection item
         * @param item The item to get the icon for
         * @returns the name of the icon for the item
         */
        getIcon(item: UserSelectionItem): string {
            if (isUserOutOfOffice(item)) return "person_off";
            if (isUser(item)) {
                if ((item.data as any)?.user_type == "service_principal") return "computer";
                if ((item.data as any)?.user_type == "system_service_principal") return "computer";
                return "person";
            }
            if (isUserGroup(item)) return "group";
            if (isTeam(item)) return "group_work";
            return "error";
        },
        /** initializes the user selector, loads filter options and starts loading items asynchronously. Does not wait for loading to complete! */
        async initAsync(): Promise<void> {
            // attach the scroll handler to the datatable
            this.attachScrollHandler();

            this.itemPool = this.createItemPool();

            this.userSelectorFilterOptions = await storeHelper.settingsStore.getSettingAsync<UserSelectorFilterOptions>(
                "user_selection_filterfields"
            );
            this.loadItemsDebounce = this.getLoadItemsDebounce();
            this.loadUserGroupsDebounce = this.getLoadUserGroupsDebounce();
            this.loadItemsAsync();
        }
    }
});
